import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(`${API}/auth/verify-email?token=${token}`);
      setStatus('success');
      setMessage(response.data.message);
      toast.success('Email verified successfully!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. Please try again.');
      toast.error('Verification failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-apple p-8 text-center">
          {/* Logo */}
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-stone-900">Bella Casa Interiors</h1>
            <p className="text-stone-500 text-sm mt-1">Project Management Software</p>
          </div>

          {status === 'verifying' && (
            <div className="py-8">
              <Loader2 className="w-16 h-16 mx-auto text-brand-primary animate-spin mb-4" />
              <h2 className="text-xl font-medium text-stone-900 mb-2">Verifying Your Email</h2>
              <p className="text-stone-500">Please wait while we verify your email address...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-8">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-xl font-medium text-stone-900 mb-2">Email Verified!</h2>
              <p className="text-stone-500 mb-6">{message}</p>
              <p className="text-sm text-stone-400 mb-4">Redirecting to login...</p>
              <Link to="/login">
                <Button className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white">
                  Go to Login
                </Button>
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="py-8">
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-medium text-stone-900 mb-2">Verification Failed</h2>
              <p className="text-stone-500 mb-6">{message}</p>
              <div className="space-y-3">
                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    Back to Login
                  </Button>
                </Link>
                <Link to="/resend-verification">
                  <Button className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white">
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Verification Email
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
